import { defineComponent as _defineComponent } from 'vue'
import MassEntryTable from "@/components/modules/entry/MassEntryTable.vue";
import EditRowsModal, {
  EditForm,
} from "@/components/modules/entry/EditRowsModal.vue";
import AccountSelectModal from "@/components/modules/account/AccountSelectModal.vue";
import CopyBetModal from "@/components/modules/entry/CopyBetModal.vue";
import { apolloClient, queries, mutations } from "@/apollo";
import { Release, User, DdPage } from "@/CodegenTypes";
import { Day, EntryInput } from "@/types";
import { parseEntryInputs } from "@/helpers/dd/parseEntryInputs";
import { calculateEntryCost } from "@/helpers/dd/calculateEntryCost";
import { calculateEntries } from "@/helpers/dd/calculateEntries";
import { getValidDayOptions } from "@/helpers/getValidDayOptions";
import { clamp } from "lodash";
import { validateNumber } from "@/helpers/dd/validateNumber";
import { formatDollar } from "@/helpers/formatDollar";
import { errorMutationNotification } from "@/utils";
import { customMoment } from "@/main";
import PageDetailsModal from "../../modules/bet-details/PageDetailsModal.vue";
import { useRootStore } from "@/store/pinia";
import ViewBetModal from "../../modules/entry/ViewBetModal.vue";
import { useTranslation } from "@/store/translation";
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute } from "vue-router/composables";

type RouteQueryValue = string | (string | null)[] | null | undefined;


export default /*#__PURE__*/_defineComponent({
  __name: 'MassEntry',
  setup(__props) {

const route = useRoute();
const rootStore = useRootStore();
const vm = getCurrentInstance()?.proxy as any;
const { $t } = useTranslation();
const t = (text: string) => $t(text);

const releases = ref<null | Release[]>(null);
const selectedDayIndex = ref(0);
const page = ref("");
const showEditModal = ref(false);
const isSubmitting = ref(false);
const showAccountSelectionModal = ref(false);
const showPageDetailsModal = ref(false);
const showCopyBetModal = ref(false);
const showViewBetModal = ref(false);
const successPageId = ref<null | DdPage["id"]>(null);
const editForm = ref({
  rowsFrom: undefined as undefined | number,
  rowsTo: undefined as undefined | number,
  big: undefined as undefined | EntryInput["big"],
  small: undefined as undefined | EntryInput["small"],
  mode: undefined as undefined | EntryInput["mode"],
});
const MAX_ROWS = 60;
const tables = [{ startingIndex: 0 }, { startingIndex: 20 }, { startingIndex: 40 }];
const entries = ref<EntryInput[]>([]);
let entriesInitialized = false;

const selectedAccountId = computed(() => getRouteQueryValue(route.query.userId));
const releasesVariables = computed(() => {
  return {
    appMode: rootStore.appMode,
  };
});
const userVariables = computed(() => {
  return {
    userId: selectedAccountId.value,
  };
});
const { result: releasesResult, loading: releasesLoading } = useQuery<{
  releases: Release[];
}>(queries.releases, releasesVariables, {
  fetchPolicy: "cache-and-network",
});
const { result: userResult, loading: userLoading } = useQuery<{ user: User }>(
  queries.userWithCreditSummary,
  userVariables,
  {
    enabled: computed(() => !!selectedAccountId.value),
    fetchPolicy: "cache-and-network",
  }
);

const user = computed(() => userResult.value?.user ?? null);
const selectedAccount = computed(() => user.value);
const apolloLoading = computed(() => releasesLoading.value || userLoading.value);
const creditForDisplay = computed(() => {
  return {
    creditGiven: selectedAccount.value?.creditGiven || 0,
    creditUsed: selectedAccount.value?.creditSummary.creditUsed || 0,
    creditBalance: selectedAccount.value?.creditSummary.creditBalance || 0,
  };
});
const selectedDay = computed<Day>({
  get() {
    if (releases.value) {
      const selectedOption =
        dayOptions.value[selectedDayIndex.value] ?? dayOptions.value[0];
      return (selectedOption?.value ?? 0) as Day;
    }
    return 0;
  },
  set(val) {
    const index = dayOptions.value.findIndex((opt) => opt.value === val);
    selectedDayIndex.value = index === -1 ? 0 : index;
  },
});
const openReleases = computed(() =>
  releases.value?.filter((release) => release.windowIsOpen)
);
const validEntries = computed(() => parseEntryInputs(entries.value));
const calculations = computed(() => calculateEntries(validEntries.value));
const validDayOptions = computed(() => {
  if (!releases.value) {
    return [];
  }
  return getValidDayOptions(releases.value);
});
const dayOptions = computed(() => {
  const OPTIONS = [
    { label: "0=<Wed, Sat, Sun>", value: 0, defaultPriority: false },
    { label: "2=<Sat, Sun>", value: 2, defaultPriority: false },
    { label: "3=<Wed>", value: 3, defaultPriority: true },
    { label: "6=<Sat>", value: 6, defaultPriority: false },
    { label: "7=<Sun>", value: 7, defaultPriority: false },
  ];

  return OPTIONS.filter((o) => validDayOptions.value.includes(o.value));
});
const totalCost = computed(() => {
  const costs = validEntries.value.map((entry) => calculateEntryCost(entry));
  const bigCosts = costs.reduce((sum, cost) => {
    sum += cost.big;
    return sum;
  }, 0);
  const smallCosts = costs.reduce((sum, cost) => {
    sum += cost.small;
    return sum;
  }, 0);

  return {
    big: bigCosts,
    small: smallCosts,
  };
});

watch(
  releasesResult,
  (result) => {
    if (result?.releases) {
      releases.value = result.releases;
      initialiseEntries();
    }
  },
  { immediate: true }
);

onMounted(async () => {
  const result = await apolloClient.query<{ releases: Release[] }>({
    query: queries.releases,
    variables: releasesVariables.value,
  });
  releases.value = result.data.releases;
  initialiseEntries();
});

function getRouteQueryValue(query: RouteQueryValue) {
  return Array.isArray(query) ? query[0] : query;
}

function initialiseEntries() {
  if (!releases.value || entriesInitialized) {
    return;
  }
  const priorityIndex = dayOptions.value.findIndex((opt) => opt.defaultPriority);
  selectedDayIndex.value = priorityIndex === -1 ? 0 : priorityIndex;
  doClearEntries();
  entriesInitialized = true;
}

function handleCopyPage(pageToCopy: DdPage) {
  doClearEntries();
  for (const [index, entry] of pageToCopy.entries.entries()) {
    entries.value[index] = {
      number: entry.number,
      day: selectedDay.value,
      big: entry.big,
      small: entry.small,
      mode: entry.mode ? entry.mode : undefined,
    };
  }
  page.value = pageToCopy.name;
  return;
}

function toggleEditModal() {
  showEditModal.value = !showEditModal.value;
}

function getDayFromDate(dateStr: string) {
  return customMoment(dateStr).isoWeekday();
}

function changeSelectedDay(newVal: Day) {
  for (const entry of entries.value) {
    entry.day = newVal;
  }
}

function focus(field: "number" | "big" | "small", inputTargetIndex: number) {
  let targetIndex = inputTargetIndex;
  if (targetIndex < 0) {
    targetIndex = 59;
  } else if (targetIndex > 59) {
    targetIndex = 0;
  }
  const tablesEles = vm.$refs["mass-entry-table"] as any;
  for (const [i, table] of tables.entries()) {
    const startingIndex = table.startingIndex;
    if (targetIndex >= startingIndex && targetIndex < startingIndex + 20) {
      tablesEles[i].focus(field, targetIndex);
    }
  }
  return;
}

function performEditRows(newEditForm: EditForm) {
  if (!newEditForm.rowsFrom || !newEditForm.rowsTo) {
    return;
  }

  const rowsFrom = clamp(newEditForm.rowsFrom, 0, MAX_ROWS);
  const rowsTo = clamp(newEditForm.rowsTo, 0, MAX_ROWS);
  const range = [rowsFrom, rowsTo].sort((a, b) => a - b);

  for (let i = range[0] - 1; i <= range[1] - 1; i++) {
    entries.value[i].big = newEditForm.big;
    entries.value[i].small = newEditForm.small;
    entries.value[i].mode = newEditForm.mode;
  }
  editForm.value = {
    rowsFrom: undefined,
    rowsTo: undefined,
    big: undefined,
    small: undefined,
    mode: undefined,
  };
  showEditModal.value = false;
}

async function clearEntries(event: Event) {
  vm.$confirm.require({
    target: event.currentTarget,
    group: "popups",
    header: "Clear the form?",
    message: "All existing inputs will be deleted.",
    icon: "pi pi-exclamation-triangle",
    accept: () => {
      doClearEntries();
    },
    reject: () => {
      // Optional: Handle rejection
    },
  });
}

function doClearEntries() {
  const newEntries = [];

  for (let i = 0; i < MAX_ROWS; i++) {
    newEntries.push({
      day: selectedDay.value,
      number: "",
      big: undefined,
      small: undefined,
      mode: undefined,
    });
  }
  entries.value = newEntries;
}

function validateAllAndShowError() {
  const errors = [];
  for (const [index, entry] of entries.value.entries()) {
    const validNumber = validateNumber(entry);
    const noSmall =
      (entry.small as any) === "" ||
      entry.small === undefined ||
      Number(entry.small) === 0;
    const noBig =
      (entry.big as any) === "" ||
      entry.big === undefined ||
      Number(entry.big) === 0;
    const hasMode = entry.mode;
    if (entry.number === "" && noSmall && noBig && !hasMode) {
      continue;
    }
    if (!validNumber) {
      errors.push(`Row ${index + 1} has invalid number`);
    }
    if (validNumber && noSmall && noBig) {
      errors.push(`Row ${index + 1} has no big and small`);
    }
  }
  return errors;
}

async function handleSubmit() {
  const errors = validateAllAndShowError();
  if (errors.length) {
    vm.$toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errors[0],
      life: 3000
    });
    return;
  }

  if (!selectedAccountId.value) {
    vm.$toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No account selected',
      life: 3000
    });
    return;
  }

  const parsedEntries = validEntries.value;

  if (!parsedEntries.length) {
    vm.$toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please enter number details for entries',
      life: 3000
    });
    return;
  }

  vm.$confirm.require({
    header: 'Confirm?',
    message: t("Are you sure to submit the entries for SG4D betting?"),
    icon: 'pi pi-exclamation-triangle',
    accept: async () => {
      try {
        isSubmitting.value = true;
        const result = await apolloClient.mutate({
          mutation: mutations.insertDdEntry,
          variables: {
            page: {
              page: page.value,
              entries: parsedEntries,
              userId: selectedAccountId.value,
              entrySource: "massEntry",
            },
          },
        });
        successPageId.value = result.data.insertDdEntry;
        showPageDetailsModal.value = true;



        doClearEntries();
        page.value = "";
      } catch (err) {
        errorMutationNotification(err);
      } finally {
        isSubmitting.value = false;
      }
    },
    reject: () => {
      // Optional: Handle rejection
    }
  });
}

function formatDate(dateStr: string) {
  return customMoment(dateStr).format("ddd, DD MMM YYYY");
}

return { __sfc: true,route, rootStore, vm, $t, t, releases, selectedDayIndex, page, showEditModal, isSubmitting, showAccountSelectionModal, showPageDetailsModal, showCopyBetModal, showViewBetModal, successPageId, editForm, MAX_ROWS, tables, entries, entriesInitialized, selectedAccountId, releasesVariables, userVariables, releasesResult, releasesLoading, userResult, userLoading, user, selectedAccount, apolloLoading, creditForDisplay, selectedDay, openReleases, validEntries, calculations, validDayOptions, dayOptions, totalCost, getRouteQueryValue, initialiseEntries, handleCopyPage, toggleEditModal, getDayFromDate, changeSelectedDay, focus, performEditRows, clearEntries, doClearEntries, validateAllAndShowError, handleSubmit, formatDate, MassEntryTable, EditRowsModal, AccountSelectModal, CopyBetModal, formatDollar, PageDetailsModal, ViewBetModal }
}

})